import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { SITE_LIVINGSOCIAL } from '../config/setup/setup';
import { createEnhancedSchema } from './createSchema';
import { getIsOpen } from './deals';
import { replaceImageServer } from './image';
import { makeUrlAbsolute } from './url';

const allowedDealIds = [
  41_074_228,
  40_567_855,
  39_496_590,
  39_418_695,
  37_275_953,
  37_238_870,
  34_982_078,
  33_647_118,
  30_013_155,
  40_365_803,
  39_770_759,
  39_471_110,
  38_154_096,
  36_996_090,
  35_002_224,
  30_065_105,
  30_012_921,
  28_201_441,
  40_151_209,
  39_940_657,
  39_842_198,
  38_031_590,
  33_977_314,
  40_998_067,
  40_798_824,
  40_444_799,
  32_582_845,
  32_097_419,
  24_206_014,
  21_423_771,
];

export function getSnippetAvailability(deal) {
  const isOpen = getIsOpen(deal);
  let totalProducts = 0;
  const getAvailableProducts = deal.products?.filter((product) => {
    totalProducts +=
      product.dailyPurchaseCapReached || product.weeklyPurchaseCapReached
        ? 0
        : product.totalRemaining;

    return totalProducts;
  });

  if (
    !isOpen ||
    (deal.totalRemaining <= 0 && totalProducts === 0) ||
    deal.soldOut
  ) {
    return 'OutOfStock';
  } else {
    return 'InStock';
  }
}

const createGshopHeadline = ({ title, extraData }) => {
  return `${title.replace('44 Locations Nationwide', extraData)}`;
};

export function parseRichSnippets(deal, productId, pdtUrl, productDetails) {
  const noShowCats = [83]; // not generated for adult category
  const router = useRouter();
  let isGoogleShopURL = false;

  if (typeof router?.query?.slug === 'object') {
    isGoogleShopURL = Boolean(
      router?.query?.slug?.find((element) => element === 'gshop'),
    );
  }

  const isLivingSocialUK = process.env.NEXT_PUBLIC_SITE === SITE_LIVINGSOCIAL;
  const isGShopOverride =
    deal?.id === 28_376_947 && isLivingSocialUK && isGoogleShopURL;

  let headline = null;
  if (isGShopOverride) {
    headline = createGshopHeadline({
      extraData: router?.query?.slug[router?.query?.slug?.length - 1],
      title: deal?.headline,
    });
  }

  if (!deal || (deal.category && noShowCats.includes(deal.category.id))) {
    return null;
  }

  // If the deal is in the allowed list, use createEnhancedSchema
  if (allowedDealIds && allowedDealIds.includes(deal.id)) {
    return JSON.stringify(createEnhancedSchema(deal, allowedDealIds));
  }

  // Continue with the existing logic for other deals
  const data = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    image: [
      productId && productDetails?.images?.[0]
        ? replaceImageServer({
            dealId: deal?.id,
            image: productDetails.images[0],
          })
        : replaceImageServer({
            dealId: deal?.id,
            image: deal.images ? deal.images[0] : null,
          }),
    ],
    name: productId
      ? productDetails?.friendlyName
      : isGShopOverride
      ? headline
      : deal.headline || '',
    offers: {
      '@type': 'Offer',
      availability: productId
        ? productDetails?.soldOut
          ? 'OutOfStock'
          : 'InStock'
        : getSnippetAvailability(deal),
      itemOffered: productId
        ? productDetails?.friendlyName
        : isGShopOverride
        ? headline
        : deal.headline || '',
      price: productId ? productDetails?.price : deal.price,
      priceCurrency: deal.currency,
      priceValidUntil: dayjs(deal.closingDate).endOf('day').toISOString(),
      url: productId ? pdtUrl : makeUrlAbsolute(deal.urlPath),
    },
  };

  if (!productId) {
    data.description = deal?.title;
  }

  // review...
  if (deal.reviewSummary && deal.reviewSummary.total === 1) {
    const review = deal.reviews[0];
    data.review = {
      author: {
        '@type': 'Person',
        name: review.nameLocation,
      },
      reviewRating: {
        ratingValue: review.rating,
      },
    };
  }

  // ...or Aggregate review
  if (deal.reviewSummary && deal.reviewSummary.total > 1) {
    data.aggregateRating = {
      ratingCount: deal.reviewSummary.total,
      ratingValue: deal.reviewSummary.average,
    };
  }

  return JSON.stringify(data);
}
